import { useEffect, useMemo, useRef, useState } from 'react';
import { Inbox, Mail, PenLine, Search, Send, X } from 'lucide-react';
import { useBrands, useLeadForms } from '../hooks/useMeta';
import { useBulkResend, useComposeEmail, useForwardLeads, useLeads, type LeadFilters } from '../hooks/useLeads';
import LeadDetail from '../components/LeadDetail';
import {
  BRAND_LABELS,
  EVENT_SKIP_REASON_LABELS,
  formatDateTime,
  NOTIFICATION_STATUS_CLASSES,
  NOTIFICATION_STATUS_LABELS,
  SKIP_REASON_LABELS,
  STATUS_CLASSES,
  STATUS_LABELS,
} from '../lib/format';
import { TEMPLATE_PLACEHOLDERS, type BrandId, type CrmStatus, type LeadListItemDto } from '@shared/types';

const CRM_STATUSES: CrmStatus[] = ['pending', 'ok', 'error', 'failed', 'skipped'];

export default function Leads() {
  const [brand, setBrand] = useState<BrandId | ''>('');
  const [form, setForm] = useState('');
  const [status, setStatus] = useState<CrmStatus | ''>('');
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState<string | null>(null);
  // Výběr pro hromadné akce — drží se napříč stránkami i filtry (ID, pořadí = pořadí označení).
  const [selected, setSelected] = useState<string[]>([]);
  const [forwardOpen, setForwardOpen] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [resendTarget, setResendTarget] = useState<'crm' | 'events' | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  // Debounce hledání — každý znak by jinak znamenal nový request na /api/leads.
  useEffect(() => {
    const timer = setTimeout(() => setQ(search.trim()), 350);
    return () => clearTimeout(timer);
  }, [search]);

  // Změna filtru vždy skočí na první stránku, jinak by uživatel skončil v prázdnu.
  useEffect(() => {
    setPage(1);
  }, [brand, form, status, q]);

  const filters: LeadFilters = useMemo(
    () => ({ brand, form, status, q, page }),
    [brand, form, status, q, page],
  );

  const { data, isLoading, isFetching } = useLeads(filters);
  const { data: forms = [] } = useLeadForms();

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const pageSize = data?.pageSize ?? 50;
  const currentPage = data?.page ?? page;
  const hasFilters = !!(brand || form || status || q);
  const from = total === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const to = Math.min(currentPage * pageSize, total);
  const lastPage = Math.max(1, Math.ceil(total / pageSize));

  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const pageIds = items.map((l) => l.id);
  const allOnPage = pageIds.length > 0 && pageIds.every((id) => selectedSet.has(id));
  const someOnPage = pageIds.some((id) => selectedSet.has(id));

  const toggle = (id: string) =>
    setSelected((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  const togglePage = () =>
    setSelected((cur) =>
      allOnPage
        ? cur.filter((id) => !pageIds.includes(id))
        : [...cur, ...pageIds.filter((id) => !cur.includes(id))],
    );

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-baseline gap-3">
        <h1 className="font-heading text-3xl font-bold text-dark">Leady</h1>
        <span className="text-sm text-gray-500">
          {isLoading ? '' : `${total} ${plural(total, 'lead', 'leady', 'leadů')}`}
        </span>
        {isFetching && !isLoading && <span className="text-xs text-gray-400">aktualizuji…</span>}
      </div>

      <div className="card mb-4 flex flex-wrap items-end gap-3 p-4">
        <label className="w-44">
          <span className="label">Značka</span>
          <select
            className="input"
            value={brand}
            onChange={(e) => setBrand(e.target.value as BrandId | '')}
          >
            <option value="">Všechny značky</option>
            <option value="buydubai">{BRAND_LABELS.buydubai}</option>
            <option value="willroi">{BRAND_LABELS.willroi}</option>
          </select>
        </label>

        <label className="w-64">
          <span className="label">Formulář</span>
          <select className="input" value={form} onChange={(e) => setForm(e.target.value)}>
            <option value="">Všechny formuláře</option>
            {forms.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </select>
        </label>

        <label className="w-44">
          <span className="label">Stav CRM</span>
          <select
            className="input"
            value={status}
            onChange={(e) => setStatus(e.target.value as CrmStatus | '')}
          >
            <option value="">Všechny stavy</option>
            {CRM_STATUSES.map((s) => (
              <option key={s} value={s}>
                {STATUS_LABELS[s]}
              </option>
            ))}
          </select>
        </label>

        <label className="ml-auto w-72">
          <span className="label">Hledat</span>
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              className="input pl-9"
              placeholder="Jméno, e-mail nebo telefon…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </label>
      </div>

      {flash && (
        <div className="mb-4 flex items-center justify-between rounded-md border border-positive/30 bg-positive-bg px-4 py-3 text-sm text-positive">
          {flash}
          <button className="text-positive/70 hover:text-positive" onClick={() => setFlash(null)}>
            <X size={16} />
          </button>
        </div>
      )}

      {selected.length > 0 && (
        <div className="card sticky top-2 z-20 mb-4 flex flex-wrap items-center gap-3 px-4 py-3">
          <span className="text-sm font-semibold text-dark">
            Vybráno: {selected.length} {plural(selected.length, 'lead', 'leady', 'leadů')}
          </span>
          <button className="btn-primary !px-4 !py-2" onClick={() => setForwardOpen(true)}>
            <Mail size={16} /> Poslat e-mailem
          </button>
          <button className="btn-secondary !px-4 !py-2" onClick={() => setComposeOpen(true)}>
            <PenLine size={16} /> Napsat e-mail leadům
          </button>
          <button className="btn-secondary !px-4 !py-2" onClick={() => setResendTarget('crm')}>
            <Send size={16} /> Odeslat do CRM
          </button>
          <button className="btn-secondary !px-4 !py-2" onClick={() => setResendTarget('events')}>
            <Send size={16} /> Odeslat do Events
          </button>
          <button className="btn-ghost ml-auto" onClick={() => setSelected([])}>
            Zrušit výběr
          </button>
        </div>
      )}

      {isLoading ? (
        <div className="card p-8 text-sm text-gray-500">Načítám…</div>
      ) : items.length === 0 ? (
        <div className="card flex flex-col items-center gap-3 p-16 text-center">
          <Inbox size={32} className="text-gray-300" />
          <div className="font-heading text-lg font-semibold text-dark">
            {hasFilters ? 'Nic nenalezeno' : 'Zatím žádné leady'}
          </div>
          <p className="max-w-sm text-sm text-gray-600">
            {hasFilters
              ? 'Zkus jiný dotaz nebo zruš filtry.'
              : 'Jakmile dorazí první lead z Meta Lead Ads, objeví se tady i s výsledkem odeslání do CRM.'}
          </p>
          {hasFilters && (
            <button
              className="btn-ghost"
              onClick={() => {
                setBrand('');
                setForm('');
                setStatus('');
                setSearch('');
              }}
            >
              Zrušit filtry
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-600">
                <tr>
                  <th className="w-10 py-3 pl-4">
                    <input
                      type="checkbox"
                      className="h-4 w-4 cursor-pointer accent-teal"
                      checked={allOnPage}
                      ref={(el) => {
                        if (el) el.indeterminate = someOnPage && !allOnPage;
                      }}
                      onChange={togglePage}
                      aria-label="Vybrat všechny leady na stránce"
                    />
                  </th>
                  <th className="px-4 py-3 text-left font-semibold">Datum</th>
                  <th className="px-4 py-3 text-left font-semibold">Jméno</th>
                  <th className="px-4 py-3 text-left font-semibold">E-mail</th>
                  <th className="px-4 py-3 text-left font-semibold">Telefon</th>
                  <th className="px-4 py-3 text-left font-semibold">Značka</th>
                  <th className="px-4 py-3 text-left font-semibold">Formulář</th>
                  <th className="px-4 py-3 text-left font-semibold">CRM</th>
                  <th className="px-4 py-3 text-left font-semibold">Events</th>
                  <th className="px-4 py-3 text-left font-semibold">E-mail leadovi</th>
                </tr>
              </thead>
              <tbody>
                {items.map((lead) => (
                  <LeadRow
                    key={lead.id}
                    lead={lead}
                    selected={selectedSet.has(lead.id)}
                    onToggle={() => toggle(lead.id)}
                    onOpen={() => setOpenId(lead.id)}
                  />
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
            <span>
              {from}–{to} z {total}
            </span>
            <div className="flex items-center gap-2">
              <button
                className="btn-ghost disabled:cursor-not-allowed disabled:opacity-40"
                disabled={currentPage <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Předchozí
              </button>
              <button
                className="btn-ghost disabled:cursor-not-allowed disabled:opacity-40"
                disabled={currentPage >= lastPage}
                onClick={() => setPage((p) => p + 1)}
              >
                Další
              </button>
            </div>
          </div>
        </>
      )}

      {openId && <LeadDetail id={openId} onClose={() => setOpenId(null)} />}
      {forwardOpen && (
        <ForwardDialog
          ids={selected}
          onClose={() => setForwardOpen(false)}
          onSent={(n, to) => {
            setForwardOpen(false);
            setSelected([]);
            setFlash(`${n} ${plural(n, 'lead odeslán', 'leady odeslány', 'leadů odesláno')} na ${to.join(', ')}.`);
          }}
        />
      )}
      {resendTarget && (
        <ResendDialog
          target={resendTarget}
          ids={selected}
          onClose={() => setResendTarget(null)}
          onDone={(summary) => {
            setResendTarget(null);
            setSelected([]);
            setFlash(summary);
          }}
        />
      )}
      {composeOpen && (
        <ComposeDialog
          ids={selected}
          // Výchozí značka: filtr, jinak značka prvního vybraného leadu na stránce.
          defaultBrand={brand || items.find((l) => selectedSet.has(l.id))?.brandId || null}
          onClose={() => setComposeOpen(false)}
          onSent={(r) => {
            setComposeOpen(false);
            setSelected([]);
            const parts = [`Odesláno ${r.sent} ${plural(r.sent, 'e-mail', 'e-maily', 'e-mailů')}`];
            if (r.skipped) parts.push(`přeskočeno ${r.skipped} (bez e-mailu nebo duplicitní adresa)`);
            if (r.failed) parts.push(`selhalo ${r.failed}`);
            setFlash(`${parts.join(', ')}.${r.detail ? ` ${r.detail}` : ''}`);
          }}
        />
      )}
    </div>
  );
}

function LeadRow({
  lead,
  selected,
  onToggle,
  onOpen,
}: {
  lead: LeadListItemDto;
  selected: boolean;
  onToggle: () => void;
  onOpen: () => void;
}) {
  return (
    <tr
      onClick={onOpen}
      className={`cursor-pointer border-t border-gray-100 transition-colors hover:bg-teal-light/40 ${
        selected ? 'bg-teal-light/30' : ''
      }`}
    >
      {/* Klik do buňky s checkboxem nesmí otevřít detail. */}
      <td className="py-3 pl-4" onClick={(e) => e.stopPropagation()}>
        <input
          type="checkbox"
          className="h-4 w-4 cursor-pointer accent-teal"
          checked={selected}
          onChange={onToggle}
          aria-label={`Vybrat lead ${lead.name || lead.email || ''}`}
        />
      </td>
      {/* Primárně čas vyplnění formuláře v Metě; čas přijetí do aplikace jen když se liší (backfill). */}
      <td className="whitespace-nowrap px-4 py-3 text-gray-600">
        {formatDateTime(lead.metaCreatedTime ?? lead.createdAt)}
        {lead.metaCreatedTime !== null && Math.abs(lead.createdAt - lead.metaCreatedTime) > 3600 && (
          <div className="text-xs text-gray-400">přijato {formatDateTime(lead.createdAt)}</div>
        )}
      </td>
      <td className="px-4 py-3 font-medium">{lead.name || <span className="text-gray-400">—</span>}</td>
      <td className="px-4 py-3 text-gray-600">{lead.email || <span className="text-gray-400">—</span>}</td>
      <td className="whitespace-nowrap px-4 py-3 text-gray-600">
        {lead.phone || <span className="text-gray-400">—</span>}
      </td>
      <td className="px-4 py-3 text-gray-600">
        {lead.brandId ? BRAND_LABELS[lead.brandId] : <span className="text-gray-400">—</span>}
      </td>
      <td className="px-4 py-3 text-gray-600">{lead.formName || lead.formId}</td>
      <td className="px-4 py-3">
        <CrmBadge
          status={lead.crmStatus}
          attempts={lead.crmAttempts}
          skipReason={lead.crmSkipReason}
        />
      </td>
      <td className="px-4 py-3">
        <EventBadge status={lead.eventStatus} skipReason={lead.eventSkipReason} />
      </td>
      <td className="px-4 py-3">
        {lead.emailLeadStatus ? (
          <span className={`badge ${NOTIFICATION_STATUS_CLASSES[lead.emailLeadStatus]}`}>
            {NOTIFICATION_STATUS_LABELS[lead.emailLeadStatus]}
          </span>
        ) : (
          <span className="text-gray-400">—</span>
        )}
      </td>
    </tr>
  );
}

/** Badge stavu CRM. U chyby ukazuje pořadí pokusu, u přeskočení důvod. */
function CrmBadge({
  status,
  attempts,
  skipReason,
}: {
  status: LeadListItemDto['crmStatus'];
  attempts: number;
  skipReason: LeadListItemDto['crmSkipReason'];
}) {
  let label = STATUS_LABELS[status];
  if (status === 'error' || status === 'failed') label = `${label} (${attempts}/5)`;
  if (status === 'skipped' && skipReason) label = `${label} — ${SKIP_REASON_LABELS[skipReason]}`;
  return <span className={`badge ${STATUS_CLASSES[status]}`}>{label}</span>;
}

/**
 * Badge stavu Events. NULL = lead vznikl dřív, než se Events zapojily — takový lead
 * nikdy doručení neměl a prázdná buňka je poctivější než „čeká".
 */
function EventBadge({
  status,
  skipReason,
}: {
  status: LeadListItemDto['eventStatus'];
  skipReason: LeadListItemDto['eventSkipReason'];
}) {
  // Vypnuté odesílání na formuláři je běžný stav (většina formulářů na event nesbírá)
  // — v seznamu by z něj byl jen šum, takže se ukáže stejně jako „nic“.
  if (!status || (status === 'skipped' && skipReason === 'disabled')) {
    return <span className="text-gray-400">—</span>;
  }
  let label = STATUS_LABELS[status];
  if (status === 'skipped' && skipReason) label = `${label} — ${EVENT_SKIP_REASON_LABELS[skipReason]}`;
  return <span className={`badge ${STATUS_CLASSES[status]}`}>{label}</span>;
}

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/** Dialog hromadného přeposlání: adresáti (čárkou / mezerou / řádkem) + volitelná poznámka. */
function ForwardDialog({
  ids,
  onClose,
  onSent,
}: {
  ids: string[];
  onClose: () => void;
  onSent: (leads: number, to: string[]) => void;
}) {
  const [toRaw, setToRaw] = useState('');
  const [note, setNote] = useState('');
  const forward = useForwardLeads();

  const to = [...new Set(toRaw.split(/[\s,;]+/).map((s) => s.trim().toLowerCase()).filter(Boolean))];
  const invalid = to.filter((e) => !EMAIL_RE.test(e));
  const canSend = to.length > 0 && invalid.length === 0 && to.length <= 10 && !forward.isPending;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const submit = () => {
    if (!canSend) return;
    forward.mutate(
      { ids, to, note: note.trim() || undefined },
      { onSuccess: (r) => onSent(r.leads, to) },
    );
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-dark/40 p-4" onClick={onClose}>
      <div className="card w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h2 className="text-xl font-bold text-dark">Poslat leady e-mailem</h2>
            <p className="mt-1 text-sm text-gray-600">
              {ids.length} {plural(ids.length, 'lead', 'leady', 'leadů')} odejde v jednom e-mailu, u každého
              všechny odpovědi z formuláře.
            </p>
          </div>
          <button className="btn-ghost !p-1" onClick={onClose} aria-label="Zavřít">
            <X size={18} />
          </button>
        </div>

        <label className="mb-4 block">
          <span className="label">Komu</span>
          <input
            className="input"
            placeholder="makler@buydubai.cz, kolega@buydubai.cz"
            value={toRaw}
            onChange={(e) => setToRaw(e.target.value)}
            autoFocus
          />
          {invalid.length > 0 ? (
            <span className="mt-1 block text-xs text-danger">Neplatná adresa: {invalid.join(', ')}</span>
          ) : to.length > 10 ? (
            <span className="mt-1 block text-xs text-danger">Najednou nejvýš 10 adresátů.</span>
          ) : (
            <span className="mt-1 block text-xs text-gray-500">Víc adres odděl čárkou. Odpovědi půjdou na tvůj e-mail.</span>
          )}
        </label>

        <label className="mb-5 block">
          <span className="label">Poznámka (nepovinné)</span>
          <textarea
            className="input min-h-[90px]"
            placeholder="Např. prosím ozvi se jim do pátku"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </label>

        {forward.isError && (
          <div className="mb-4 rounded-md border border-danger/40 bg-danger/10 p-3 text-sm text-danger">
            {forward.error.message}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button className="btn-ghost" onClick={onClose}>
            Zrušit
          </button>
          <button className="btn-primary" disabled={!canSend} onClick={submit}>
            <Mail size={16} /> {forward.isPending ? 'Odesílám…' : 'Odeslat'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Hromadné odeslání do CRM / Events s průběhem. Po doběhnutí ukáže souhrn
 * a teprve zavřením se výběr zruší (onDone), aby šlo výsledek v klidu přečíst.
 */
function ResendDialog({
  target,
  ids,
  onClose,
  onDone,
}: {
  target: 'crm' | 'events';
  ids: string[];
  onClose: () => void;
  onDone: (summary: string) => void;
}) {
  const [includeDelivered, setIncludeDelivered] = useState(false);
  const [done, setDone] = useState(0);
  const resend = useBulkResend();
  const name = target === 'crm' ? 'CRM' : 'Events';
  const running = resend.isPending;

  const results = resend.data ?? [];
  const count = (pred: (r: (typeof results)[number]) => boolean) => results.filter(pred).length;
  const already = count((r) => r.alreadyDelivered);
  const ok = count((r) => !r.alreadyDelivered && r.status === 'ok');
  const skipped = count((r) => r.status === 'skipped');
  const failed = count((r) => r.status === 'error' || r.status === 'failed');
  const summary =
    `${name}: odesláno ${ok}` +
    (already ? `, už doručeno dřív ${already}` : '') +
    (skipped ? `, přeskočeno ${skipped}` : '') +
    (failed ? `, chyba ${failed} (zkusí se znovu automaticky, dokud nevyčerpá pokusy)` : '') +
    '.';

  const start = () => {
    setDone(0);
    resend.mutate({ target, ids, includeDelivered, onProgress: setDone });
  };

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-dark/40 p-4"
      onClick={running ? undefined : onClose}
    >
      <div className="card w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-xl font-bold text-dark">Odeslat do {name}</h2>

        {!resend.isSuccess && !resend.isError ? (
          <>
            <p className="mt-2 text-sm text-gray-600">
              {ids.length}{' '}
              {plural(ids.length, 'vybraný lead se odešle', 'vybrané leady se odešlou', 'vybraných leadů se odešle')}{' '}
              do {name} stejně jako tlačítkem v detailu leadu — i z formulářů, které mají {name}{' '}
              vypnuté.
              {target === 'crm' && ' Notifikace makléřům dostanou jen leady, které je ještě nikdy neměly.'}
            </p>
            <label className="mt-4 flex cursor-pointer items-start gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={includeDelivered}
                disabled={running}
                onChange={(e) => setIncludeDelivered(e.target.checked)}
              />
              <span>
                Odeslat znovu i leady, které už v {name} úspěšně jsou
                {target === 'crm' && (
                  <span className="block text-xs text-danger">V CRM tím vznikne duplicitní task.</span>
                )}
              </span>
            </label>
            {running && (
              <div className="mt-5">
                <div className="h-2 overflow-hidden rounded-full bg-gray-100">
                  <div
                    className="h-full bg-teal transition-all"
                    style={{ width: `${Math.round((done / ids.length) * 100)}%` }}
                  />
                </div>
                <div className="mt-1 text-xs text-gray-500">
                  {done} / {ids.length}
                </div>
              </div>
            )}
            <div className="mt-6 flex justify-end gap-2">
              <button className="btn-ghost" disabled={running} onClick={onClose}>
                Zrušit
              </button>
              <button className="btn-primary" disabled={running} onClick={start}>
                <Send size={16} /> {running ? 'Odesílám…' : `Odeslat ${ids.length}`}
              </button>
            </div>
          </>
        ) : (
          <>
            {resend.isError ? (
              <div className="mt-3 rounded-md border border-danger/40 bg-danger/10 p-3 text-sm text-danger">
                Odesílání se přerušilo po {done} z {ids.length}: {resend.error.message}
              </div>
            ) : (
              <p className="mt-3 text-sm text-gray-700">{summary}</p>
            )}
            {(skipped > 0 || failed > 0) && (
              <p className="mt-2 text-xs text-gray-500">
                Důvod u jednotlivých leadů uvidíš ve sloupci {name} v seznamu nebo v detailu leadu.
              </p>
            )}
            <div className="mt-6 flex justify-end">
              <button
                className="btn-primary"
                onClick={() => (resend.isError ? onClose() : onDone(summary))}
              >
                Hotovo
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/** Dialog vlastního e-mailu: každý vybraný lead dostane svůj e-mail s dosazenými proměnnými. */
function ComposeDialog({
  ids,
  defaultBrand,
  onClose,
  onSent,
}: {
  ids: string[];
  defaultBrand: BrandId | null;
  onClose: () => void;
  onSent: (r: { sent: number; skipped: number; failed: number; detail: string }) => void;
}) {
  const { data: brands = [] } = useBrands();
  const [brandId, setBrandId] = useState<BrandId | ''>(defaultBrand ?? '');
  const [from, setFrom] = useState('');
  const [replyTo, setReplyTo] = useState('');
  const [subject, setSubject] = useState('');
  const [text, setText] = useState('');
  // Odesílatel a reply-to se předvyplní ze značky, dokud je uživatel sám nepřepíše.
  const [fromTouched, setFromTouched] = useState(false);
  const [replyTouched, setReplyTouched] = useState(false);
  const textRef = useRef<HTMLTextAreaElement>(null);
  const compose = useComposeEmail();

  const brandRow = brands.find((b) => b.id === brandId);
  useEffect(() => {
    if (!fromTouched) setFrom(brandRow?.emailFrom ?? '');
    if (!replyTouched) setReplyTo(brandRow?.replyTo ?? '');
  }, [brandRow, fromTouched, replyTouched]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const fromOk = /^(?:[^<>@]*<\s*)?[^@\s<>]+@[^@\s<>]+\.[^@\s<>]+(?:\s*>)?$/.test(from.trim());
  const replyOk = !replyTo.trim() || EMAIL_RE.test(replyTo.trim());
  const canSend = fromOk && replyOk && !!subject.trim() && !!text.trim() && !compose.isPending;

  /** Vloží proměnnou na pozici kurzoru v textu. */
  const insert = (key: string) => {
    const el = textRef.current;
    const token = `{${key}}`;
    if (!el) return setText((t) => t + token);
    const start = el.selectionStart ?? text.length;
    const end = el.selectionEnd ?? text.length;
    setText(text.slice(0, start) + token + text.slice(end));
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start + token.length, start + token.length);
    });
  };

  const submit = () => {
    if (!canSend) return;
    compose.mutate(
      {
        ids,
        brandId: brandId || null,
        from: from.trim(),
        replyTo: replyTo.trim() || null,
        subject: subject.trim(),
        text,
      },
      { onSuccess: onSent },
    );
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-dark/40 p-4" onClick={onClose}>
      <div
        className="card max-h-[92vh] w-full max-w-2xl overflow-y-auto p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h2 className="text-xl font-bold text-dark">Napsat e-mail leadům</h2>
            <p className="mt-1 text-sm text-gray-600">
              Každý z {ids.length} {plural(ids.length, 'vybraného leadu', 'vybraných leadů', 'vybraných leadů')}{' '}
              dostane vlastní e-mail. Leady bez e-mailu se přeskočí.
            </p>
          </div>
          <button className="btn-ghost !p-1" onClick={onClose} aria-label="Zavřít">
            <X size={18} />
          </button>
        </div>

        <div className="mb-4 grid gap-3 md:grid-cols-[10rem_1fr]">
          <label className="block">
            <span className="label">Značka</span>
            <select
              className="input"
              value={brandId}
              onChange={(e) => setBrandId(e.target.value as BrandId | '')}
            >
              <option value="">Bez značky</option>
              {brands.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="label">Odesílatel</span>
            <input
              className="input"
              placeholder="BuyDubai <info@buydubai.cz>"
              value={from}
              onChange={(e) => {
                setFrom(e.target.value);
                setFromTouched(true);
              }}
            />
            {from.trim() && !fromOk ? (
              <span className="mt-1 block text-xs text-danger">Zadej adresu nebo „Jméno &lt;adresa&gt;“.</span>
            ) : (
              <span className="mt-1 block text-xs text-gray-500">
                Doména adresy musí být ověřená v Resendu.
              </span>
            )}
          </label>
        </div>

        <label className="mb-4 block">
          <span className="label">Odpovědi na (reply-to)</span>
          <input
            className="input"
            placeholder="makler@buydubai.cz"
            value={replyTo}
            onChange={(e) => {
              setReplyTo(e.target.value);
              setReplyTouched(true);
            }}
          />
          {!replyOk && <span className="mt-1 block text-xs text-danger">Neplatná adresa.</span>}
        </label>

        <label className="mb-4 block">
          <span className="label">Předmět</span>
          <input
            className="input"
            placeholder="Např. Pozvánka na prezentaci, {jmeno}"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          />
        </label>

        <label className="mb-2 block">
          <span className="label">Text</span>
          <textarea
            ref={textRef}
            className="input min-h-[200px]"
            placeholder={'Dobrý den, {jmeno},\n\n…'}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
        </label>
        <div className="mb-5 flex flex-wrap items-center gap-1.5 text-xs text-gray-500">
          Vložit:
          {TEMPLATE_PLACEHOLDERS.map((k) => (
            <button
              key={k}
              type="button"
              className="rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 font-mono text-gray-700 hover:border-teal hover:text-teal"
              onClick={() => insert(k)}
            >
              {`{${k}}`}
            </button>
          ))}
          <span className="ml-1">Prázdný řádek = nový odstavec.</span>
        </div>

        {compose.isError && (
          <div className="mb-4 rounded-md border border-danger/40 bg-danger/10 p-3 text-sm text-danger">
            {compose.error.message}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button className="btn-ghost" onClick={onClose}>
            Zrušit
          </button>
          <button className="btn-primary" disabled={!canSend} onClick={submit}>
            <Mail size={16} />{' '}
            {compose.isPending ? 'Odesílám…' : `Odeslat ${ids.length} ${plural(ids.length, 'leadovi', 'leadům', 'leadům')}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function plural(n: number, one: string, few: string, many: string): string {
  if (n === 1) return one;
  if (n >= 2 && n <= 4) return few;
  return many;
}
